import React, { useState } from "react";
import { Form } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import axios from "axios";
import Cookies from "universal-cookie";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { toast } from "react-toastify";
import { Background, setUser, useSetUserLoggedIn } from "../LoginComp";
import { selected_authentication_server } from "../../../deployment-config";
import { FloatLabelInput, LoadingButton } from "../../../Utils/CustomComponent";
import { commonToast } from "../../../Utils";
import urls from "../../../Utils/urls";
import ForgotPasswordModal from "../ForgotPasswordModal";
import useMfaLogin from "./useMfaLogin";
import MfaVerifyStep from "./MfaVerifyStep";
import SetupHeader from "./SetupHeader";
import PhoneSetupStep from "./PhoneSetupStep";
import useFooterClearance from "./useFooterClearance";
import MfaHelpLine from "./MfaHelpLine";
import { markNudgePending } from "../../Profile/security/nudge/nudgeStorage";
import EnrollAuthenticatorModal from "../../Profile/security/EnrollAuthenticatorModal";
import AddMobileStep from "../../Profile/security/AddMobileStep";

const getFingerprint = async () => (await (await FingerprintJS.load()).get()).visitorId;

// Same trusted-device call as LoginComp.trustDevice, after a successful verify.
const registerTrustedDevice = async (userId) => {
  try {
    const fingerprint = await getFingerprint();
    await axios.post(urls.dashboard.registerDevice, JSON.stringify({ userId, fingerprint }), {
      headers: { "Content-Type": "application/json" },
    });
    toast.success("Device successfully registered as a trusted device!", { ...commonToast });
  } catch (error) {
    console.error("Trusted device registration failed:", error?.message);
    toast.error("Trusted device registration failed!", { ...commonToast });
  }
};

// Login behind MFA_LOGIN_V2 (picked in pages/Login/index.js). LoginComp is untouched.
// The session is stored only after /auth/mfa/verify; the password is never stored.
const MfaLoginComp = () => {
  const { register, handleSubmit, watch, formState: { errors } } = useForm();
  const values = watch();
  const dispatch = useDispatch();
  const [cookies] = useState(new Cookies());
  const setUserLoggedIn = useSetUserLoggedIn();
  const [trustedDevice, setTrustedDevice] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const footerClearance = useFooterClearance();

  const onSession = async (session) => {
    // `config.data` keeps the shape older screens read from localStorage["user"] (username
    // only — never the password; see docs/plans/authenticator-mfa.md, ChangePassword note).
    const email = localStorage.getItem("email_for_forgot_password");
    const res = { data: session, config: { data: JSON.stringify({ username: email }) } };
    await setUser({ res, setIsLoggedIn: () => {}, cookies, dispatch });
    markNudgePending(); // the signed-in shell asks MfaNudgeHost for ONE prompt (lane 7)
    if (trustedDevice) await registerTrustedDevice(session.userid);
    const res2 = await axios.post(
      selected_authentication_server.access_account.endpoint,
      { userid: localStorage.getItem("apex_userid") },
      selected_authentication_server.access_account.headers,
    );
    setUserLoggedIn({ res: res2 });
  };

  const mfa = useMfaLogin({ getFingerprint, onSession });
  const inMfa = mfa.stage !== "credentials";
  // Login setup (docs/specs/login-setup.md §2): the phone step, or the authenticator dialog it leads to.
  const inSetup = mfa.stage === "phone" || (mfa.stage === "enroll" && mfa.inSetup);

  const onSubmit = (data) => {
    localStorage.setItem("email_for_forgot_password", data.email);
    mfa.start(data.email, data.password);
  };

  return (
    <Background>
      <form className="w-75 mfa-login-form" style={{ paddingBottom: footerClearance }} onSubmit={handleSubmit(onSubmit)}>
        <h3>Customer Portal</h3>
        <p className="pb-4 mb-3">Please enter your login details to proceed.</p>
        <div className="form-outline mb-4">
          <FloatLabelInput
            register={{ ...register("email", { required: true }) }}
            label="Email"
            value={values.email}
            disabled={inMfa || mfa.busy}
            defaultValue=""
            error={errors.email ? "Email Required" : ""}
            type="email"
          />
        </div>
        {inMfa ? (
          <>
            {inSetup && <SetupHeader stepLabel={mfa.setup.stepLabel} expiresAt={mfa.setup.expiresAt} />}
            {mfa.stage === "phone" && (
              <PhoneSetupStep {...mfa.setup} busy={mfa.busy} error={mfa.error} onSend={mfa.setup.sendPhone}
                onConfirm={mfa.setup.confirmPhone} onChangeNumber={mfa.setup.changeNumber} />
            )}
            {!inSetup && <MfaVerifyStep
              {...mfa}
              expired={mfa.stage === "expired"}
              onSelectMethod={mfa.selectMethod}
              onSubmit={mfa.verify}
              onRestart={mfa.restart}
              onResend={mfa.resend}
              afterSubmit={
                // As on UAT's OTP step (LoginComp.jsx): a plain checkbox, blue label, under the button.
                <div className="text-start linkClick text-decoration-underline mb-2">
                  <Form.Check
                    type="checkbox"
                    id="trusted-device"
                    label="Remember this device for next 30 days"
                    checked={trustedDevice}
                    onChange={(e) => setTrustedDevice(e.target.checked)}
                  />
                </div>
              }
            />}
            {/* SSN/TIN fallback: the Security page's own setup dialogs, reused on the login screen. */}
            {mfa.stage === "enroll" && mfa.enrollment && (
              <EnrollAuthenticatorModal
                enrollment={mfa.enrollment}
                busy={mfa.busy}
                error={mfa.enrollError}
                onConfirm={mfa.confirmEnrollment}
                onCancel={mfa.inSetup ? mfa.restart : mfa.cancelEnrollment}
                onRestart={mfa.inSetup ? mfa.setup.newQr : mfa.cancelEnrollment}
                stepLabel={mfa.enrollStepLabel}
              />
            )}
            {mfa.stage === "mobile" && <AddMobileStep token={mfa.mobileToken} onDone={mfa.finishMobileStep} />}
            <MfaHelpLine method={mfa.method} />
          </>
        ) : (
          <>
            <div className="form-outline mb-1" style={{ position: "relative" }}>
              <FloatLabelInput
                register={{ ...register("password", { required: true }) }}
                label="Password"
                value={values.password}
                disabled={mfa.busy}
                defaultValue=""
                error={errors.password ? "Password Required" : ""}
                type={showPassword ? "text" : "password"}
              />
              <button
                type="button"
                className="btn btn-link position-absolute"
                style={{ top: 6, right: 8 }}
                onClick={() => setShowPassword((s) => !s)}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
            <div className="mb-4"><ForgotPasswordModal /></div>
            <LoadingButton
              className="btn btn-primary w-100 btn-block fa-lg gradient-custom-2 mb-3 text-white"
              type="submit"
              disabled={mfa.busy}
              loading={mfa.busy}
              title="Continue"
            />
            {mfa.error && <div className="alert alert-error" role="alert">{mfa.error}</div>}
          </>
        )}
      </form>
    </Background>
  );
};

export default MfaLoginComp;
